(ns frontend.test-ribbon.view
  (:require
   [ui.util :as uu]
   [reagent.core :as r]
   [re-frame.core :as rf]
   [frontend.routing :as routing]
   [frontend.view.not-found :as not-found]
   [frontend.state.current-session :as session]
   [ui.state.session.sub :as session-sub]
   [ui.components.radix-ui.context-menu.view :as context-menu]
   [frontend.test-ribbon.sub :as sub]
   [ui.view.nev.view :as nev-view]
   [frontend.test-ribbon.event :as event]
   [ui.components.button.view :as button]
   [oops.core :as oops]
   [frontend.view.syncing-data :as syncing-data]
   [common.sync-progress :as sync-progress]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [common.util :as cu]
   [common.localization :as loc]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn create-ribbon-dialog
  [_]
  (let [ref-atom (atom nil)
        close #(oops/ocall @ref-atom "close")
        title-atom (r/atom "")]
    (fn []
      (let [close (fn []
                    (reset! title-atom "")
                    (close))]
        [:dialog
         {:id "create-ribbon-modal"
          :ref #(reset! ref-atom %)
          :class "p-0 rounded border"
          :on-click close}
         [:div
          {:class "flex flex-col gap-2 p-3"
           :on-click (fn [e]
                       (oops/ocall e "preventDefault")
                       (oops/ocall e "stopPropagation"))}
          [labelled-input/view
           {:label (loc/tr :ribbon-name)
            :data-type :data.type/text-simple
            :value @title-atom
            :on-change (fn [value]
                         (reset! title-atom value))}]
          [:div
           {:class "flex gap-1 justify-center"}
           [button/sm-secondary-hollow
            {:on-click close}
            (loc/tr :cancel)]
           [button/sm-primary-solid
            {:on-click (fn []
                         (rf/dispatch [::event/create-ribbon @title-atom])
                         (close))
             :disabled? (cu/empty-value? @title-atom)}
            (loc/tr :create-ribbon)]]]]))))

(defn cannot-delete-last-ribbon-modal
  []
  (let [ref-atom (atom nil)]
    (fn []
      [:dialog
       {:id "cannot-delete-last-ribbon-modal"
        :ref #(reset! ref-atom %)
        :class "p-0 rounded border"}
       [:div
        {:class "p-3 "}
        [:p (loc/tr :you-cannot-delete-last-ribbon)]
        [button/sm-secondary-hollow
         {:on-click #(oops/ocall @ref-atom "close")}
         (loc/tr :ok)]]])))

(defn add-new-tab-modal
  []
  (let [ref-atom (atom nil)
        close #(oops/ocall @ref-atom "close")
        make-default-tab-atom (r/atom false)]
    (fn []
      [:dialog
       {:id "add-new-tab-modal"
        :ref #(reset! ref-atom %)
        :class "p-0 rounded border"
        :on-click close}
       [:div
        {:class "p-3"
         :on-click (fn [e]
                     (oops/ocall e "preventDefault")
                     (oops/ocall e "stopPropagation"))}
        [:p (loc/tr :what-would-you-like-to-see)]
        [:p
         {:class "text-sm m-0 text-base-light-text-paler"}
         (loc/tr :you-can-only-add-company-grid-tabs)]
        [button/primary-open
         {:on-click (fn []
                      (rf/dispatch [::event/create-grid-tab
                                    {:entity-type :entity.type/company
                                     :make-default-tab? @make-default-tab-atom}])
                      (close))}
         (loc/tr :companies-grid)]]])))

(defn clone-ribbon-modal
  []
  (let [ref-atom (atom nil)
        name-atom (r/atom nil)
        close (fn []
                (oops/ocall @ref-atom "close")
                (reset! name-atom nil))]
    (fn []
      (let [current-ribbon-name @(rf/subscribe [::sub/current-ribbon-name])
            disabled? (or (nil? @name-atom)
                          (= @name-atom current-ribbon-name))]
        [:dialog
         {:id "clone-ribbon-modal"
          :ref #(reset! ref-atom %)
          :class "p-0 rounded border"
          :on-click close}
         [:div
          {:on-click (fn [e]
                       (oops/ocall e "preventDefault")
                       (oops/ocall e "stopPropagation"))
           :class "p-3 flex flex-col gap-2 w-96"}
          [:p
           {:class "text-lg font-semibold"}
           (loc/tr :clone-ribbon)]

          [labelled-input/view
           {:label (loc/tr :ribbon-name)
            :data-type :data.type/text-simple
            :value (if-not (nil? @name-atom)
                     @name-atom
                     current-ribbon-name)
            :on-change (fn [value]
                         (reset! name-atom value))}]

          (when (and (not (nil? @name-atom))
                     (= @name-atom current-ribbon-name))
            [:p
             {:class "text-sm m-0 text-base-light-text-paler"}
             (loc/tr :cloned-ribbon-cannot-have-same-name)])

          [:div
           {:class "flex gap-2 justify-end"}
           [button/sm-secondary-hollow
            {:on-click close}
            (loc/tr :cancel)]
           [button/sm-primary-solid
            {:disabled? disabled?
             :on-click (fn []
                         (rf/dispatch [::event/clone-ribbon @name-atom])
                         (close))}
            (loc/tr :clone-ribbon)]]]]))))

(defn add-new-tab-button
  []
  (let [all-ribbons-count @(rf/subscribe [::sub/all-ribbons-count])
        disabled? (zero? all-ribbons-count)]
    [:<>
     [:button
      {:class (uu/cm "w-10 h-10 flex items-center justify-center border"
                     (when-not disabled? "hover:bg-base-regular"))
       :on-click #(rf/dispatch [::event/open-add-new-tab-modal])
       :disabled disabled?}
      "+"]]))

(defn tab-comp
  [{:keys [tab last-item?]}]
  (let [tab-id (:db/id tab)
        tab-state @(rf/subscribe [::sub/tab-state tab-id])
        {:keys [title kicker focused? mandatory? default-tab?]} tab-state
        move-tab-to-another-ribbon-options @(rf/subscribe [::sub/move-tab-to-another-ribbon-options])
        default-tab-count @(rf/subscribe [::sub/current-ribbon-default-tab-count])

        context-menu-items
        (concat
         [{:label (loc/tr :duplicate-tab)
           :on-click #(rf/dispatch [::event/duplicate-tab tab-id])}]
         (when (and default-tab? (< 1 default-tab-count))
           [{:label (loc/tr :demote-from-default-tabs)
             :on-click #(rf/dispatch [::event/demote-tab-from-default-tabs tab-id])}])
         (when (and (not default-tab?) (seq move-tab-to-another-ribbon-options))
           [{:label (loc/tr :move-to-another-ribbon)
             :disabled? (empty? move-tab-to-another-ribbon-options)
             :sub-menu? true
             :items (->> move-tab-to-another-ribbon-options
                         (mapv (fn [{:keys [value label]}]
                                 {:value value
                                  :label label
                                  :on-click #(rf/dispatch [::event/update-tab
                                                           {:tab-id tab-id
                                                            :field :tab/ribbon
                                                            :value value}])})))}])

         (when-not default-tab?
           [{:label (loc/tr :make-default-tab-for-ribbon)
             :on-click #(rf/dispatch [::event/make-tab-default-tab tab-id])}]))]
    [:div
     {:class "flex h-full"}
     [context-menu/context-menu
      [context-menu/trigger
       {:asChild true}
       [:div
        {:class (uu/cm "flex items-center pl-6 gap-3 pr-2 py-2"
                       "relative bg-base-light"
                       "cursor-pointer"
                       (when default-tab?
                         "h-full bg-secondary-regular hover:bg-secondary-gloomy text-secondary-regular-text")

                       (when (and default-tab? focused?)
                         "bg-secondary-gloomy")

                       (when-not default-tab?
                         "h-10 bg-base-regular")
                       (when-not (or default-tab? focused?)
                         "hover:bg-base-regular")
                       (when focused?
                         "z-10 outline outline-3 outline-primary-regular")

                       (when (and focused? (not default-tab?)) "bg-base-light"))
         :on-click (fn [e]
                     (rf/dispatch [::event/open-tab tab-id]))}
        [:div
         [:p
          {:class (uu/cm "text-sm m-0 leading-none"
                         (when-not default-tab?
                           "text-base-light-text-paler")
                         (when default-tab?
                           "text-secondary-regular-text-paler"))}

          kicker]
         [:p.leading-none.font-medium title]]

        [:div
         (when-not default-tab?
           [:button
            {:class (uu/cm "p-0 h-5 w-5 flex items-center justify-center rounded full"
                           (if focused?
                             "hover:bg-base-regular"
                             "hover:bg-base-light"))

             :disabled (boolean mandatory?)
             :on-click (fn [e]
                         (oops/ocall e "stopPropagation")
                         (rf/dispatch [::event/close-tab tab-id]))
             :data-type "close-tab-btn"}
            [:i.fal.fa-times.text-xs.m-0]])]]]

      [context-menu/content
       {:class "p-1 bg-base-light rounded border shadow-md z-50"}
       (->> context-menu-items
            (map (fn [{:keys [label sub-menu? items on-click disabled?]}]
                   (cond
                     sub-menu?
                     ^{:key (str label)}
                     [context-menu/sub
                      [context-menu/sub-trigger
                       {:class "px-2 py-2 rounded hover:bg-base-regular cursor-default flex items-center gap-3"}
                       [:span label]
                       [:i.fal.fa-angle-right
                        {:class "text-xl"}]]
                      [context-menu/sub-content
                       {:class "z-[51] bg-base-light rounded border shadow-md p-1"}
                       (->> items
                            (map (fn [{:keys [label on-click]}]
                                   ^{:key (str "sub-" label)}
                                   [context-menu/item
                                    {:class "px-2 py-2 rounded hover:bg-base-regular cursor-default"
                                     :on-click on-click}
                                    [:span label]])))]]
                     :else
                     ^{:key (str label)}
                     [context-menu/item
                      {:class (uu/cm "px-2 py-2 rounded cursor-default"
                                     (when-not disabled? "hover:bg-base-regular")
                                     (when disabled? "text-base-light-text-paler"))
                       :on-click on-click
                       :disabled disabled?}
                      [:span label]]))))]]]))

(defn tabs-bar
  []
  (let [default-tabs @(rf/subscribe [::sub/current-ribbon-default-tabs])
        temp-tabs @(rf/subscribe [::sub/current-ribbon-temp-tabs])]
    [:div
     {:class "flex bg-white items-stretch pt-2 border border-x-transparent border-t-transparent max-w-screen overflow-hidden overflow-x-auto"}
     [:div
      {:class "flex divide-x divide-solid shrink-0 divide-secondary-regular-border-paler divide-x-2"}
      (->> default-tabs
           (map (fn [{:keys [db/id] :as tab}]
                  ^{:key (str id)}
                  [:div
                   {:class "shrink-0"}
                   [tab-comp
                    {:tab tab
                     :default-tab? true}]])))]
     [:div
      {:class "flex flex-wrap pt-3 divide-x divide-x-2 divide-y divide-solid divide-base-gloomy"}
      (->> temp-tabs
           (map-indexed
            (fn [index {:keys [db/id] :as tab}]
              (let [last-item? (= index (dec (count temp-tabs)))]
                ^{:key (str id)}
                [:div
                 {:class "shrink-0 flex"}
                 [tab-comp
                  {:tab tab
                   :default-tab? false}]
                 (when last-item?
                   [add-new-tab-button])]))))
      (when (empty? temp-tabs)
        [add-new-tab-button])]]))

(defn grid-view
  []
  (let [companies @(rf/subscribe [::session-sub/entities-for-type :entity.type/company])
        open-nev (fn [entity open-in-new-tab?]
                   (rf/dispatch [::event/open-nev
                                 {:entity-id (:db/id entity)
                                  :entity-type :entity.type/company
                                  :open-in-new-tab? open-in-new-tab?}]))]
    [:div.p-5
     [:p.mb-2.text-xl.font-bold
      (loc/tr :companies-grid)]
     [:table
      {:class "table-auto border border-collapse bg-base-light"}
      [:thead
       [:tr
        [:th.p-2.border.bg-base-regular
         (loc/tr :name)]]]
      [:tbody
       (->> companies
            (map (fn [company]
                   (let [id (:db/id company)
                         name (:asset/name company)]
                     ^{:key id}
                     [:tr
                      [:td.border.p-2
                       [:button
                        {:class "underline hover:text-base-light-text-accent"
                         :on-click #(open-nev company false)}
                        name]]]))))]]]))

(defn no-tabs-view
  []
  (let []
    [:div
     {:class "relative h-full flex flex-col gap-10 items-center justify-center"}
     [:h3
      {:class "font-bold"}
      (loc/tr :no-tabs-open)]
     [:div
      {:class "absolute top-5 left-3 flex items-center gap-2"}
      [:i
       {:class "fal fa-arrow-up text-3xl animate-bounce font-medium"}]
      [:div
       (loc/tr :open-new-tab)]]]))

(defn no-ribbons-view
  []
  [:div
   {:class "h-full w-full flex flex-col justify-center items-center gap-5"}
   [:h3
    {:class "text-2xl"}
    (loc/tr :no-ribbons-yet)]
   [button/primary-solid
    {:on-click #(rf/dispatch [::event/open-create-ribbon-modal])}
    (loc/tr :create-ribbon)]])

(defn page-content
  []
  (let [current-tab @(rf/subscribe [::sub/current-tab])
        current-view-type @(rf/subscribe [::sub/current-view-type])
        tab-ids @(rf/subscribe [::sub/current-ribbon-tab-ids])
        all-ribbons-count @(rf/subscribe [::sub/all-ribbons-count])]
    [:div
     {:class "flex flex-col h-full max-h-full overflow-hidden overflow-y-scroll"}
     (cond
       (zero? all-ribbons-count)
       [no-ribbons-view]

       (empty? tab-ids)
       [no-tabs-view]

       (not current-tab)
       [:div (loc/tr :no-tab-selected)]

       (= current-view-type :view.type/single-entity)
       [:div
        {:class "grow overflow-hidden"}
        [nev-view/view
         {:view-change-callback-fn
          (fn [{:keys [view-id]}]
            (rf/dispatch [::event/set-tab-current-view view-id]))}]]

       :else
       [grid-view])]))

(defn ribbon-view
  []
  [:div
   {:class "flex flex-col grow overflow-hidden"}
   [:div
    {:class "shadow"}
    [tabs-bar]]
   [:div
    {:class "grow flex flex-col overflow-hidden"}
    [page-content]]])

(defn component
  [props]
  [:div
    ;; TODO fix height hack
   {:class "flex flex-col h-screen grow max-h-[calc(100vh-52px)] overflow-hidden"}
   [ribbon-view props]
   [clone-ribbon-modal]
   [add-new-tab-modal]
   [create-ribbon-dialog]
   [cannot-delete-last-ribbon-modal]])

(defn test-ribbon-view
  [location args]
  (rf/dispatch-sync [::event/init! location args])
  (r/create-class
   {:reagent-render
    (fn [location]
      (let []
        [component
         {:location location}]))}))

(defn page-guard
  [element]
  (if-not (session/nev-v3?)
    [not-found/not-found-view]
    [syncing-data/wait-until-sync-progress sync-progress/BASE
     [default-page-layout/view
      [:<> element]]]))

(defmethod routing/render-view :test-ribbon-home
  [{:keys [view-id] :as args}]
  ^{:key (str view-id)}
  [page-guard
   [test-ribbon-view :home args]])

(defmethod routing/render-view :test-ribbon-grid
  [{:keys [view-id] :as args}]
  ^{:key (str view-id)}
  [page-guard
   [test-ribbon-view :grid args]])

(defmethod routing/render-view :test-ribbon-nev
  [{:keys [view-id] :as args}]
  ^{:key (str view-id)}
  [page-guard
   [test-ribbon-view :nev args]])
